import { useNavigator } from '@repo/toolkit/use-navigator'
import { FormIcon } from '@repo/ui/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { getEnrollment } from '../../../api/enrollments/get-enrollment'
import { updateEnrollment } from '../../../api/enrollments/update-enrollment'
import { ItemNotFound } from '../../../components/elements/item-not-found'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import UserNavBar from '../../../components/elements/nav-bar'
import EnrollmentQuotationForm from '../../../components/pages/enrollments/enrollment-quotation-form'
import { mapEnrollmentToEnrollmentQuotationInput } from '../../../components/pages/enrollments/utils/enrollments-utils'
import { Paths } from '../../../lib/constants/path'
import { enrollmentQuotationFields } from '../../../lib/types/fields/enrollments/enrollment'
import { EnrollmentProperties } from '../../../lib/types/models/enrollment/enrollment.properties'
import {
  EnrollmentInput,
  EnrollmentQuotationInput
} from '../../../lib/types/models/enrollment/enrollment-input'
import { UpdateInput } from '../../../lib/types/utils'
import { updateButtonsConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import NotFoundPage from '../../common/not-found'

const EnrollmentQuotationUpdatePage = () => {
  const { id } = useParams<{ id: string }>()
  const { navigate, back } = useNavigator()
  const queryClient = useQueryClient()

  if (!id || isNaN(+id)) return <ItemNotFound title="Identifiant de devis invalide" />

  const { mutateAsync: updateEnrollmentMutation } = useMutation({
    mutationFn: (input: UpdateInput<EnrollmentInput>) => updateEnrollment(input, ['id']),
    onError: showAxiosError,
    onSuccess() {
      queryClient.invalidateQueries({ exact: true, queryKey: ['enrollment-quotation', id] })
      toast.success('Devis modifié avec succès')
    }
  })

  const { data, isLoading } = useQuery({
    queryKey: ['enrollment-quotation', id, enrollmentQuotationFields],
    queryFn: () => getEnrollment(+id, enrollmentQuotationFields)
  })

  // Update the enrollment first and then the proposition
  const handleSubmit = async (values: EnrollmentQuotationInput) => {
    // Enrollment ID is required
    if (!id) throw new Error('Enrollment ID is required')
    // the OTO Individual proposition is persisted on change, not on save
    const { proposition, otoIndividualProposition: _oto, ...enrollmentData } = values
    const enrollment = await updateEnrollmentMutation({
      ...enrollmentData,
      declaredFriends: proposition?.declaredFriends,
      id: +id
    })
    return enrollment
  }

  if (isLoading) return <LoadingScreen />
  if (!data) return <NotFoundPage />

  if (!data.mainProposition && !data.otoIndividualProposition)
    return <ItemNotFound title="Ce devis n'a pas de proposition principale" />

  return (
    <div>
      <UserNavBar title={EnrollmentProperties.getEnrollmentTitle(data)} icon={FormIcon} />

      <div className={'h-4'} />
      <EnrollmentQuotationForm
        mode={'update'}
        enrollmentId={+id}
        propositionId={data.mainProposition?.id}
        defaultValues={mapEnrollmentToEnrollmentQuotationInput(data, {
          cseCode: data.cseRegistration?.cse?.code
        })}
        buttonsConfig={updateButtonsConfig({
          onCancel: () => back(Paths.ENROLLMENT_QUOTATION_LIST_PAGE),
          onUpdate: async (values) => {
            await handleSubmit(values)
          },
          onUpdateThenCreate: async (values) => {
            await handleSubmit(values)
            navigate(Paths.ENROLLMENT_QUOTATION_CREATE_PAGE)
          },
          onUpdateThenList: async (values) => {
            await handleSubmit(values)
            back(Paths.ENROLLMENT_QUOTATION_LIST_PAGE)
          }
        })}
      />
    </div>
  )
}

export default EnrollmentQuotationUpdatePage
